import React, { useState, useEffect } from 'react';
import { budgetSetupService } from '../../services/budgetSetupService';

const emptyForm = { currency_code: '', currency_name: '', conversion_to_base: '', is_base: false, display_color: '#E0E0E0' };

const CurrencySetupModal = ({ onClose }) => {
  // Currencies are per budget version, so everything hangs off selectedVersion.
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'add'
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  // Pending inline rate edits: { [currency_code]: string }
  const [rateEdits, setRateEdits] = useState({});

  // Load budget versions once; default to the first.
  useEffect(() => {
    budgetSetupService.getVersions()
      .then((res) => {
        if (res.success) {
          setVersions(res.data);
          if (res.data.length > 0) setSelectedVersion((v) => v || res.data[0].version_id);
        }
      })
      .catch((error) => console.error('Failed to load versions', error));
  }, []);

  const load = async (versionId = selectedVersion) => {
    if (!versionId) { setCurrencies([]); return; }
    setIsLoading(true);
    try {
      const res = await budgetSetupService.getCurrencies(versionId);
      if (res.success) setCurrencies(res.data);
    } catch (error) {
      console.error('Failed to load currencies', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reload currencies whenever the selected version changes.
  useEffect(() => { load(selectedVersion); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedVersion]);

  const hasBase = currencies.some((c) => c.is_base);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!selectedVersion) { setFormError('Select a budget version first.'); return; }
    try {
      await budgetSetupService.addCurrency({
        version_id: selectedVersion,
        currency_code: formData.currency_code,
        currency_name: formData.currency_name,
        conversion_to_base: formData.is_base ? 1 : formData.conversion_to_base,
        is_base: formData.is_base,
        display_color: formData.display_color,
      });
      setFormData(emptyForm);
      setViewMode('list');
      load();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Failed to save currency');
    }
  };

  const handleSetBase = async (code) => {
    try {
      await budgetSetupService.setBaseCurrency(code, selectedVersion);
      load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to set base currency');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete currency ${code}?`)) return;
    try {
      await budgetSetupService.deleteCurrency(code, selectedVersion);
      load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete currency');
    }
  };

  // Reflect the swatch live while the user picks (local only, no API spam).
  const handleColorInput = (code, color) => {
    setCurrencies((prev) => prev.map((c) => (c.currency_code === code ? { ...c, display_color: color } : c)));
  };

  // Persist once, when the picker closes / loses focus.
  const handleColorCommit = async (code, color) => {
    try {
      await budgetSetupService.updateCurrency(code, { display_color: color, version_id: selectedVersion });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update colour');
      load();
    }
  };

  const handleRateSave = async (code) => {
    const raw = rateEdits[code];
    if (raw === undefined) return;
    const rate = Number(raw);
    if (Number.isNaN(rate) || rate <= 0) {
      alert('Conversion rate must be a positive number.');
      return;
    }
    try {
      await budgetSetupService.updateCurrency(code, { conversion_to_base: rate, version_id: selectedVersion });
      setRateEdits((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update rate');
    }
  };

  const inputClass =
    'w-full p-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-md outline-none focus:ring-2 focus:ring-slate-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans">
      <div className="bg-white dark:bg-slate-800 w-full max-w-[880px] rounded-xl shadow-2xl flex flex-col h-[80vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Manage Currencies</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            ✕
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0 gap-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Budget Version</label>
            <select
              value={selectedVersion}
              onChange={(e) => { setSelectedVersion(e.target.value); setViewMode('list'); }}
              className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-slate-400"
            >
              {versions.length === 0 && <option value="">No versions available</option>}
              {versions.map((v) => (
                <option key={v.version_id} value={v.version_id}>
                  {v.version_code}{v.description ? ` — ${v.description}` : ''}
                </option>
              ))}
            </select>
            <button
              onClick={() => { setFormError(null); setViewMode(viewMode === 'list' ? 'add' : 'list'); }}
              disabled={!selectedVersion}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {viewMode === 'list' ? '+ New Currency' : '← Back to List'}
            </button>
          </div>
          {viewMode === 'list' && !isLoading && !hasBase && currencies.length > 0 && (
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
              No base currency set — choose one below.
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'add' ? (
            <div className="max-w-2xl mx-auto p-6">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">Add Currency</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Conversion rate = value of 1 unit of this currency in the base currency.
                E.g. if the base is ZAR, then USD = <span className="font-mono">18.50</span> means 1 USD = 18.50 ZAR.
              </p>
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                  {formError}
                </div>
              )}
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Currency Code</label>
                    <input
                      required type="text" maxLength={3} placeholder="e.g. USD"
                      value={formData.currency_code}
                      onChange={(e) => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
                      className={`${inputClass} uppercase tracking-wider`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Currency Name</label>
                    <input
                      required type="text" maxLength={100} placeholder="e.g. US Dollar"
                      value={formData.currency_name}
                      onChange={(e) => setFormData({ ...formData, currency_name: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Conversion Rate to Base</label>
                  <input
                    type="number" step="0.000001" min="0" placeholder="e.g. 18.50"
                    value={formData.is_base ? 1 : formData.conversion_to_base}
                    disabled={formData.is_base}
                    required={!formData.is_base}
                    onChange={(e) => setFormData({ ...formData, conversion_to_base: e.target.value })}
                    className={`w-1/2 ${inputClass} ${formData.is_base ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Display Colour</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.display_color}
                      onChange={(e) => setFormData({ ...formData, display_color: e.target.value })}
                      className="h-9 w-12 p-0 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 cursor-pointer"
                    />
                    <span className="font-mono text-sm text-slate-600 dark:text-slate-300 uppercase">{formData.display_color}</span>
                    <span className="text-xs text-slate-400">Used to tint this currency in grids and reports.</span>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formData.is_base}
                    onChange={(e) => setFormData({ ...formData, is_base: e.target.checked })}
                    className="w-4 h-4 accent-slate-900"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    Set as the base currency (rate fixed at 1.000000; replaces any existing base)
                  </span>
                </label>

                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setViewMode('list')} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm text-white bg-slate-900 rounded-md hover:bg-slate-800">Save Currency</button>
                </div>
              </form>
            </div>
          ) : (
            <div className="p-6">
              {isLoading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
              ) : currencies.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-slate-500 dark:text-slate-400">No currencies yet. Add your base currency to get started.</p>
                </div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 pr-4 font-semibold">Code</th>
                      <th className="py-2 pr-4 font-semibold">Name</th>
                      <th className="py-2 pr-4 font-semibold">Colour</th>
                      <th className="py-2 pr-4 font-semibold">Rate to Base</th>
                      <th className="py-2 pr-4 font-semibold">Status</th>
                      <th className="py-2 pr-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {currencies.map((c) => {
                      const pending = rateEdits[c.currency_code];
                      const rateValue = pending !== undefined ? pending : c.conversion_to_base;
                      return (
                        <tr key={c.currency_code} className="text-slate-800 dark:text-slate-100">
                          <td className="py-3 pr-4 font-mono font-semibold">{c.currency_code}</td>
                          <td className="py-3 pr-4">{c.currency_name}</td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={c.display_color || '#E0E0E0'}
                                onChange={(e) => handleColorInput(c.currency_code, e.target.value)}
                                onBlur={(e) => handleColorCommit(c.currency_code, e.target.value)}
                                title={`Colour for ${c.currency_code}`}
                                className="h-7 w-9 p-0 border border-slate-300 dark:border-slate-600 rounded cursor-pointer bg-white dark:bg-slate-700"
                              />
                              <span className="font-mono text-xs text-slate-500 dark:text-slate-400 uppercase">{c.display_color || '#E0E0E0'}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="number" step="0.000001" min="0"
                                value={rateValue}
                                disabled={c.is_base}
                                onChange={(e) => setRateEdits((prev) => ({ ...prev, [c.currency_code]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleRateSave(c.currency_code); }}
                                className={`w-32 p-1.5 border rounded-md bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 outline-none focus:ring-2 focus:ring-slate-400 ${c.is_base ? 'opacity-60 cursor-not-allowed' : ''}`}
                              />
                              {pending !== undefined && !c.is_base && (
                                <button
                                  onClick={() => handleRateSave(c.currency_code)}
                                  className="px-2 py-1 text-xs font-medium text-white bg-slate-900 rounded hover:bg-slate-800"
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            {c.is_base ? (
                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Base</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center justify-end gap-2">
                              {!c.is_base && (
                                <button
                                  onClick={() => handleSetBase(c.currency_code)}
                                  className="px-2.5 py-1 text-xs font-medium rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600"
                                >
                                  Set as Base
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(c.currency_code)}
                                disabled={c.is_base}
                                title={c.is_base ? 'Set another currency as base before deleting this one' : 'Delete'}
                                className={`px-2.5 py-1 text-xs font-medium rounded ${c.is_base ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : 'text-red-700 bg-red-50 hover:bg-red-100'}`}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurrencySetupModal;
